// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { forwardRef, type TextareaHTMLAttributes } from 'react'
import { cn } from '@/lib/utils'

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?:      string
  error?:      string
  hint?:       string
  showCount?:  boolean
  maxLength?:  number
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, hint, showCount, maxLength, className, value, id, ...props }, ref) => {
    const textareaId = id ?? label?.toLowerCase().replace(/\s+/g, '-')
    const currentLength = typeof value === 'string' ? value.length : 0

    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label htmlFor={textareaId} className="text-sm font-medium text-ink-2">
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={textareaId}
          value={value}
          maxLength={maxLength}
          aria-invalid={!!error}
          className={cn(
            'w-full rounded-xl border bg-white px-4 py-3',
            'text-sm text-ink placeholder:text-ink-4',
            'resize-none transition-colors duration-150',
            'focus:outline-none focus:ring-2 focus:ring-sage-500/20 focus:border-sage-500',
            'min-h-[120px]',
            error
              ? 'border-status-alertBorder'
              : 'border-sand-300 hover:border-sage-300',
            className,
          )}
          {...props}
        />
        <div className="flex justify-between items-center">
          <div>
            {error && (
              <p className="text-xs text-status-alertText" role="alert">{error}</p>
            )}
            {!error && hint && (
              <p className="text-xs text-ink-4">{hint}</p>
            )}
          </div>
          {showCount && maxLength && (
            <span className={cn(
              'text-xs',
              currentLength > maxLength * 0.9 ? 'text-status-warnText' : 'text-ink-4',
            )}>
              {currentLength}/{maxLength}
            </span>
          )}
        </div>
      </div>
    )
  },
)

Textarea.displayName = 'Textarea'
